/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from 'zustand';
import type { Song, SongWithSections, SongArrangement } from '@proclaim/shared';
import { api } from '../shared/api';
import { useToastStore } from './toast-store';

interface SongState {
  songs: Song[];
  currentSong: SongWithSections | null;
  searchResults: Song[];
  loading: boolean;

  fetchSongs: (background?: boolean) => Promise<void>;
  fetchSong: (id: string, background?: boolean) => Promise<void>;
  createSong: (data: any) => Promise<Song>;
  updateSong: (id: string, data: any) => Promise<void>;
  deleteSong: (id: string) => Promise<void>;
  searchSongs: (query: string) => Promise<void>;
  updateSection: (sectionId: string, data: { type?: string; label?: string; lyrics?: string }) => Promise<void>;
  createArrangement: (songId: string, name: string, sectionOrder: string[]) => Promise<SongArrangement>;
  updateArrangement: (songId: string, arrangementId: string, data: { name?: string; sectionOrder?: string[] }) => Promise<SongArrangement>;
  deleteArrangement: (songId: string, arrangementId: string) => Promise<void>;
}

const toast = () => useToastStore.getState();

export const useSongStore = create<SongState>((set, get) => ({
  songs: [],
  currentSong: null,
  searchResults: [],
  loading: false,

  fetchSongs: async (background = false) => {
    if (!background) set({ loading: true });
    try {
      const songs = await api.getSongs();
      set({ songs, loading: false });
    } catch {
      set({ loading: false });
      if (!background) toast().show('error', 'No se pudieron cargar las canciones');
    }
  },

  fetchSong: async (id, background = false) => {
    if (!background) set({ loading: true });
    try {
      const song = await api.getSong(id);
      set({ currentSong: song, loading: false });
    } catch (e: any) {
      set({ loading: false });
      if ((e as any)?.status === 404) {
        set({ currentSong: null });
        return;
      }
      if (!background) toast().show('error', 'No se pudo cargar la canción');
    }
  },

  createSong: async (data) => {
    try {
      const song = await api.createSong(data);
      set((s) => ({ songs: [...s.songs, song] }));
      return song;
    } catch (e: any) {
      toast().show('error', 'No se pudo crear la canción');
      throw e;
    }
  },

  updateSong: async (id, data) => {
    try {
      await api.updateSong(id, data);
      get().fetchSongs();
    } catch (e: any) {
      toast().show('error', 'No se pudo guardar la canción');
      throw e;
    }
  },

  deleteSong: async (id) => {
    try {
      await api.deleteSong(id);
      set((s) => ({ songs: s.songs.filter((song) => song.id !== id) }));
    } catch (e: any) {
      toast().show('error', 'No se pudo eliminar la canción');
      throw e;
    }
  },

  searchSongs: async (query) => {
    if (!query.trim()) {
      set({ searchResults: [] });
      return;
    }
    try {
      const searchResults = await api.searchSongs(query);
      set({ searchResults });
    } catch {
      toast().show('error', 'Error al buscar canciones');
    }
  },

  updateSection: async (sectionId, data) => {
    try {
      const updated = await api.updateSongSection(sectionId, data);
      set((s) => {
        if (!s.currentSong) return {};
        return {
          currentSong: {
            ...s.currentSong,
            sections: s.currentSong.sections.map((sec) => sec.id === sectionId ? { ...sec, ...updated } : sec),
          },
        };
      });
    } catch (e: any) {
      toast().show('error', 'No se pudo guardar la sección');
      throw e;
    }
  },

  createArrangement: async (songId, name, sectionOrder) => {
    try {
      const arrangement = await api.createArrangement(songId, name, sectionOrder);
      set((s) =>
        s.currentSong?.id === songId
          ? { currentSong: { ...s.currentSong!, arrangements: [...s.currentSong!.arrangements, arrangement] } }
          : {}
      );
      return arrangement;
    } catch (e: any) {
      toast().show('error', 'No se pudo crear la disposición');
      throw e;
    }
  },

  updateArrangement: async (songId, arrangementId, data) => {
    try {
      const arrangement = await api.updateArrangement(songId, arrangementId, data);
      set((s) =>
        s.currentSong?.id === songId
          ? { currentSong: { ...s.currentSong!, arrangements: s.currentSong!.arrangements.map((a) => a.id === arrangementId ? arrangement : a) } }
          : {}
      );
      return arrangement;
    } catch (e: any) {
      toast().show('error', 'No se pudo actualizar la disposición');
      throw e;
    }
  },

  deleteArrangement: async (songId, arrangementId) => {
    try {
      await api.deleteArrangement(songId, arrangementId);
      set((s) =>
        s.currentSong?.id === songId
          ? { currentSong: { ...s.currentSong!, arrangements: s.currentSong!.arrangements.filter((a) => a.id !== arrangementId) } }
          : {}
      );
    } catch (e: any) {
      toast().show('error', 'No se pudo eliminar la disposición');
      throw e;
    }
  },
}));
